import React, { ReactNode, useEffect } from "react"

import { Sentry } from "@izettle/react-spa"

import { CustomEventTrackData, EventTracker } from "@/events/tracking-event"

import { getEventForId } from "./tracking"

type TrackingContextType = {
  onTrackClicks: () => void
  offTrackClicks: () => void
  trigger: (data?: CustomEventTrackData) => void
}

export const EventTrackingContext = React.createContext<TrackingContextType>({
  onTrackClicks: () => null,
  offTrackClicks: () => null,
  trigger: () => null,
})

type ProviderProps = {
  children: ReactNode
  isActive: boolean
}

type WindowsProps = typeof window & {
  izPushEvent: (
    domain: string,
    subdomain: string,
    page: string,
    action: string,
    payload?: Record<string, unknown>,
  ) => void
}

export const EventTrackingProvider = (props: ProviderProps) => {
  const { children, isActive } = props
  const { on, off, onTrackClicks, offTrackClicks, trigger } = EventTracker()

  const onEventTrackingListener = (event: any) => {
    let eventForId = null
    if (event.type === "click" && event.currentTarget?.hasAttribute("data-track-id")) {
      const trackId = event.currentTarget.getAttribute("data-track-id")
      const trackData = event.currentTarget.getAttribute("data-track-payload")
      const payload = JSON.parse(trackData)
      eventForId = getEventForId(trackId, payload)
    } else if (event?.detail) {
      const { trackId, payload } = event.detail
      eventForId = getEventForId(trackId, payload)
    }
    if (eventForId) {
      const { domain, subdomain, page, action, payload } = eventForId
      try {
        ;(window as WindowsProps)?.izPushEvent(domain, subdomain, page, action, payload)
      } catch (err) {
        Sentry.captureException(err)
      }
    }
  }

  useEffect(() => {
    if (isActive) {
      on(onEventTrackingListener)
      return () => {
        off(onEventTrackingListener)
      }
    }
  }, [children, isActive, off, on])

  return (
    <EventTrackingContext.Provider
      value={{
        onTrackClicks: onTrackClicks(onEventTrackingListener),
        offTrackClicks: offTrackClicks(onEventTrackingListener),
        trigger,
      }}
    >
      {children}
    </EventTrackingContext.Provider>
  )
}
